import { useSettings } from '@wu/widget-core';
import { useTranslation } from 'react-i18next';
import type { SalaryPeriod, SalaryValue } from '../../types';
import { formatSalaryAmount } from '../../utils';

type formatPeriodType = (
  period: SalaryPeriod,
  t: (key: string) => string
) => string;

export const formatPeriod: formatPeriodType = (period, t) => {
  const periodTranslations = {
    hour: t('per hour'),
    day: t('per day'),
    week: t('per week'),
    month: t('per month'),
    year: t('per year'),
    shift: t('per shift')
  } as const;

  const nbsp = '\u00A0';
  return periodTranslations[period].replaceAll(' ', nbsp);
};

export function useFormatSalaryValue() {
  const { t } = useTranslation();
  const { language } = useSettings();
  const { language: locale = 'en' } = language;

  return (salaryValue: SalaryValue) => {
    const {
      type,
      period,
      customSalary,
      minSalary,
      maxSalary,
      amount,
      currency
    } = salaryValue;

    const translatedPeriod = formatPeriod(period, t);
    const formattedAmount = formatSalaryAmount({ amount, currency, locale });
    const formattedMinSalary = formatSalaryAmount({
      amount: minSalary,
      currency,
      locale
    });
    const formattedMaxSalary = formatSalaryAmount({
      amount: maxSalary,
      currency,
      locale
    });

    switch (type) {
      case 'fixed':
        return `${formattedAmount} ${translatedPeriod}`;
      case 'from':
        return `${t('From {{amount}}', {
          amount: formattedAmount
        })} ${translatedPeriod}`;
      case 'upto':
        return `${t('Up to {{amount}}', {
          amount: formattedAmount
        })} ${translatedPeriod}`;
      case 'range':
        return `${formattedMinSalary} - ${formattedMaxSalary} ${translatedPeriod}`;
      case 'custom':
        return customSalary || '';
      case 'none':
        return '';
      default:
        return '';
    }
  };
}
